import { Layer, ZIndexes } from "@common/constants";
import { random, randomFloat, randomRotation } from "@common/utils/random";
import { Vec, type Vector } from "@common/utils/vector";
import { Container, Graphics } from "pixi.js";
import { CameraManager } from "../managers/cameraManager";
import { ParticleManager, type ParticleOptions } from "../managers/particleManager";
import { SoundManager } from "../managers/soundManager";
import { softwareRendering } from "../utils/graphicsAcceleration";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";

/*
 * The Gravity Gloves' plasma burst, as it looks and sounds (the bolt itself is a bullet, `gravity_gloves_bullet`,
 * flown by objects/bullet.ts with its tail, `gravity_plasma_trail`):
 *  - leaving the hands: a white flash, a ring of light, sparks in a cone the way it goes, a crackle of little arcs and
 *    the sound `gravity_plasma_fire`
 *  - in flight: a glowing orb on the tail's head that flickers and crackles with arcs, shedding sparks
 *  - striking something: a flash, a ring, sparks thrown off the surface (all round off a player), arcs, and
 *    `gravity_plasma_hit`; at the end of its range it fizzles out instead (`gravity_plasma_fizzle`)
 * It glows: added light (blendMode "add"), so it brightens what's under it, drawn in two layers of its own on each
 * floor: under the players (the bolt, its light, rings, crackling arcs and sparks) and over them (the white-hot core
 * of a flash, which has to show on top of a body). (Drawn by Pixi's Canvas 2D renderer, without WebGL, added light
 * last in a frame used to wash the next frame out: see utils/canvasBlend.ts.) The sounds are files like every weapon's
 * (audio/game/shared/weapons), heard and panned by where they are; a little pitch spread keeps a stream of shots
 * from sounding mechanical. Drawn without a graphics card, the sparks are halved.
 */

const ARC_CORE = 0xe6f8ff;
const ARC_GLOW = 0x4fb8ff;
const SPARK_TINTS = [0xffffff, 0xe6f8ff, 0xa8e2ff, 0x7fd4ff] as const;
/** its two layers on each floor: over the bullets' tails and under the downed and standing players; over the players, under bushes */
const UNDER_Z = ZIndexes.Bullets + 0.5;
const OVER_Z = ZIndexes.Players + 0.45;

/** each floor's two layers of glow (made when first needed; always in that floor's container of the moment) */
const roots = new Map<Layer, { readonly under: Container, readonly over: Container }>();
const root = (layer: Layer, which: "under" | "over"): Container => {
    let pair = roots.get(layer);
    if (!pair || pair.under.destroyed || pair.over.destroyed) {
        const under = new Container(), over = new Container();
        under.zIndex = UNDER_Z;
        over.zIndex = OVER_Z;
        pair = { under, over };
        roots.set(layer, pair);
    }
    // (which container a floor is drawn in depends on the floor the viewer is on: it can change)
    const container = CameraManager.getContainer(layer);
    for (const part of [pair.under, pair.over]) if (part.parent !== container) container.addChild(part);
    return pair[which];
};

const rich = (): boolean => !softwareRendering();
const count = (n: number): number => (rich() ? n : Math.ceil(n / 2));

/**
 * near enough to the camera to be seen (a widest view is about 70 units across from its middle): the effects of a shot
 * far off the screen aren't drawn (25 players firing it at once made hundreds of particles nobody saw). Its sound
 * plays anyway: that's for the sound system to judge by distance
 */
const VISIBLE_RANGE = 90;
const onScreen = (position: Vector): boolean =>
    Vec.len(Vec.sub(position, Vec.scale(CameraManager.position, 1 / PIXI_SCALE))) < VISIBLE_RANGE;

/** a particle of light (added light), under the players or over them */
const glow = (options: ParticleOptions, which: "under" | "over" = "under"): void => {
    const { image } = ParticleManager.spawnParticle(options);
    image.blendMode = "add";
    root(options.layer ?? Layer.Ground, which).addChild(image);
};

/** a white-hot flash (`scale` from, to), gone in `lifetime` ms: its bloom on the ground, its core over any body */
const flash = (position: Vector, layer: Layer, scale: readonly [number, number], lifetime: number): void => {
    glow({
        frames: "gravity_plasma_orb",
        position,
        layer,
        zIndex: 1,
        lifetime,
        speed: Vec(0, 0),
        rotation: 0,
        scale: { start: scale[0], end: scale[1] },
        alpha: { start: 1, end: 0, ease: t => t * t }
    });
    glow({
        frames: "gravity_plasma_orb",
        position,
        layer,
        zIndex: 0,
        lifetime: lifetime * 0.8,
        speed: Vec(0, 0),
        rotation: 0,
        scale: { start: scale[0] * 0.42, end: scale[1] * 0.3 },
        alpha: { start: 0.9, end: 0, ease: t => t * t }
    }, "over");
};

/** a big soft light round the spot, brightening the ground for a moment */
const light = (position: Vector, layer: Layer, scale: number, alpha: number, lifetime: number): void => {
    glow({
        frames: "gravity_plasma_orb",
        position,
        layer,
        zIndex: 0,
        lifetime,
        speed: Vec(0, 0),
        rotation: 0,
        scale: { start: scale * 0.85, end: scale },
        alpha: { start: alpha, end: 0 }
    });
};

/** a ring of light spreading out (`scale` from, to) */
const ring = (position: Vector, layer: Layer, scale: readonly [number, number], lifetime: number): void => {
    glow({
        frames: "gravity_plasma_ring",
        position,
        layer,
        zIndex: 0,
        lifetime,
        speed: Vec(0, 0),
        rotation: randomRotation(),
        scale: { start: scale[0], end: scale[1], ease: t => 1 - (1 - t) ** 3 },
        alpha: { start: 0.95, end: 0, ease: t => t ** 1.5 }
    });
};

/** sparks flying `heading` ± `spread` (radians), each a short streak along its way */
const sparks = (
    n: number,
    position: Vector,
    layer: Layer,
    heading: number,
    spread: number,
    speed: readonly [number, number],
    lifetime: readonly [number, number],
    scale: readonly [number, number]
): void => {
    for (let i = 0; i < count(n); i++) {
        const angle = heading + randomFloat(-spread, spread);
        glow({
            frames: "gravity_plasma_spark",
            position,
            layer,
            zIndex: 2,
            lifetime: random(lifetime[0], lifetime[1]),
            speed: Vec.fromPolar(angle, randomFloat(speed[0], speed[1])),
            rotation: angle,
            scale: { start: randomFloat(scale[0], scale[1]), end: 0.08 },
            alpha: { start: 1, end: 0 },
            tint: SPARK_TINTS[random(0, SPARK_TINTS.length - 1)]
        });
    }
};

/** a jagged little lightning arc out of (0, 0), `length` pixels toward `angle` (on the graphics' current path) */
const arc = (g: Graphics, angle: number, length: number): void => {
    const steps = 4;
    const cos = Math.cos(angle), sin = Math.sin(angle);
    g.moveTo(0, 0);
    for (let i = 1; i <= steps; i++) {
        const along = (length * i) / steps;
        const across = randomFloat(-0.5, 0.5) * (length / steps) * (i === steps ? 0.4 : 1);
        g.lineTo(cos * along - sin * across, sin * along + cos * across);
    }
};

/** draws `n` crackling arcs (fresh every frame: they flicker) */
const crackle = (g: Graphics, n: number, min: number, max: number): void => {
    g.clear();
    for (let i = 0; i < n; i++) arc(g, randomRotation(), randomFloat(min, max));
    g.stroke({ width: 7, color: ARC_GLOW, alpha: 0.32, cap: "round", join: "round" });
    g.stroke({ width: 2.4, color: ARC_CORE, alpha: 0.95, cap: "round", join: "round" });
};

/** a plasma burst's bolt in flight: the glowing orb on its tail's head, crackling, shedding sparks (objects/bullet.ts) */
export class PlasmaBolt {
    private readonly _halo = new TrenchioSprite("gravity_plasma_orb");
    private readonly _orb = new TrenchioSprite("gravity_plasma_orb");
    private readonly _arcs = new Graphics();
    private _layer?: Layer;
    private _lastSpark = 0;
    private _gone = false;

    constructor(position: Vector, layer: Layer, private readonly _heading: number) {
        for (const part of [this._halo, this._orb, this._arcs]) part.blendMode = "add";
        this._halo.zIndex = 0;
        this._orb.zIndex = 1;
        this._arcs.zIndex = 2;
        this.follow(position, layer);
    }

    /** every frame while it flies: where it is now */
    follow(position: Vector, layer: Layer): void {
        if (this._gone) return;
        // (on a stair it moves to the other floor with its tail)
        const container = root(layer, "under");
        if (layer !== this._layer || this._orb.parent !== container) {
            this._layer = layer;
            for (const part of [this._halo, this._orb, this._arcs]) container.addChild(part);
        }
        const at = toPixiCoords(position);
        const flicker = 0.88 + Math.random() * 0.24;
        this._orb.setVPos(at).setScale(0.95 * flicker);
        this._halo.setVPos(at).setScale(2.3 * (0.92 + Math.random() * 0.16)).setAlpha(0.3 + Math.random() * 0.14);
        this._arcs.position.set(at.x, at.y);
        crackle(this._arcs, random(2, 3), 15, 28);

        const now = Date.now();
        if (now - this._lastSpark >= 12 && onScreen(position)) {
            this._lastSpark = now;
            // shed sideways and a little back, as it tears through the air
            const back = this._heading + Math.PI;
            sparks(1, position, layer, back + (Math.random() < 0.5 ? -1 : 1) * 1.1, 0.6, [2, 7], [110, 230], [0.45, 0.7]);
        }
    }

    destroy(): void {
        if (this._gone) return;
        this._gone = true;
        this._halo.destroy();
        this._orb.destroy();
        this._arcs.destroy();
    }
}

/** arcs crackling round a spot for a moment (the hands, a strike) */
interface Crackle {
    readonly graphics: Graphics
    readonly until: number
    readonly arcs: number
    readonly min: number
    readonly max: number
}

class PlasmaFxClass {
    private readonly _crackles = new Set<Crackle>();

    /** a bolt for a plasma burst's bullet (it follows the bullet; objects/bullet.ts) */
    bolt(position: Vector, layer: Layer, heading: number): PlasmaBolt {
        return new PlasmaBolt(position, layer, heading);
    }

    /** the burst leaves someone's hands at `position`, flying `heading` (radians, from the right) */
    muzzle(position: Vector, heading: number, layer: Layer): void {
        SoundManager.play("gravity_plasma_fire", { position, layer, falloff: 1, maxRange: 160, speed: randomFloat(0.95, 1.05) });
        if (!onScreen(position)) return;
        flash(position, layer, [1.9, 0.5], 120);
        light(position, layer, 4.6, 0.45, 190);
        ring(position, layer, [0.15, 0.95], 180);
        sparks(12, position, layer, heading, 0.5, [10, 26], [120, 260], [0.7, 1.1]);
        this._crackle(position, layer, 100, 3, 18, 34);
    }

    /** the bolt struck something at `position`; `normal`: the way off its surface (radians); `player`: it hit a player */
    impact(position: Vector, normal: number, layer: Layer, player: boolean): void {
        SoundManager.play("gravity_plasma_hit", { position, layer, falloff: 1, maxRange: 110, speed: randomFloat(0.92, 1.08) });
        if (!onScreen(position)) return;
        flash(position, layer, [1.5, 2.6], 150);
        light(position, layer, 5.8, 0.5, 250);
        ring(position, layer, [0.2, 1.5], 260);
        // off a wall or a box they fly back out of it; off a player, all round
        sparks(18, position, layer, normal, player ? Math.PI : 1.25, [8, 28], [160, 340], [0.7, 1.2]);
        this._crackle(position, layer, 160, 4, 20, 40);
    }

    /** the bolt ran out of range at `position` (flying `heading`): it fizzles out */
    fizzle(position: Vector, heading: number, layer: Layer): void {
        SoundManager.play("gravity_plasma_fizzle", { position, layer, falloff: 1.2, maxRange: 70 });
        if (!onScreen(position)) return;
        flash(position, layer, [1.1, 0.1], 180);
        light(position, layer, 3, 0.25, 200);
        sparks(8, position, layer, heading, 1.7, [2, 8], [160, 300], [0.5, 0.8]);
    }

    private _crackle(position: Vector, layer: Layer, ms: number, arcs: number, min: number, max: number): void {
        const graphics = new Graphics();
        graphics.blendMode = "add";
        graphics.zIndex = 3;
        const at = toPixiCoords(position);
        graphics.position.set(at.x, at.y);
        root(layer, "under").addChild(graphics);
        const entry: Crackle = { graphics, until: Date.now() + ms, arcs, min, max };
        crackle(graphics, arcs, min, max);
        this._crackles.add(entry);
    }

    /** every frame (from GravityFx.update): the crackling arcs flicker, and go when their moment is over */
    update(): void {
        const now = Date.now();
        for (const entry of this._crackles) {
            if (now >= entry.until) {
                entry.graphics.destroy();
                this._crackles.delete(entry);
                continue;
            }
            crackle(entry.graphics, entry.arcs, entry.min, entry.max);
        }
    }

    /** a reset of the view (from GravityFx.reset) */
    reset(): void {
        for (const entry of this._crackles) entry.graphics.destroy();
        this._crackles.clear();
        // (what's still in them is only let go of: the particles' pictures are ParticleManager's to destroy, the
        // bolts' their bullets')
        for (const pair of roots.values()) {
            pair.under.destroy();
            pair.over.destroy();
        }
        roots.clear();
    }
}

export const PlasmaFx = new PlasmaFxClass();
